import { Button } from "@arco-design/web-react";
import {getAuth, OAuthProvider, signInWithPopup} from "firebase/auth";
import { useDispatch } from "react-redux";
import { setUser } from "../store/userSlice";
import { endpoints } from "../api/endpoints";
import axios from "axios";
import { routes } from "../routes/routes";
import { completeAuthRedirect } from "../utils/authReturnUrl";
import { Message } from "@arco-design/web-react";
import { WindowsOutlined } from "@ant-design/icons"
import { isAuthPopupClosedByUser } from "../utils/isAuthPopupClosedByUser";
import { splitFullName } from "../utils/nameUtils";
import { FirebaseError } from "firebase/app";
import { useTranslation } from "react-i18next";
import { getOAuthButtonProps } from "../utils/oauthButtonStyle";
import { trackSignUp } from "../analytics/track";

type MicrosoftRegisterButtonProps = {
    isMentor: boolean;
    isGuest?: boolean;
    variant?: "default" | "icon";
    signupSource?: string;
    redirectPath?: string;
};

export const MicrosoftRegisterButton = ({ isMentor, isGuest = false, variant = "default", signupSource, redirectPath }: MicrosoftRegisterButtonProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const handleMicrosoftRegister = async () => {
        const auth = getAuth();
        const provider = new OAuthProvider("microsoft.com");

        try {
            const result = await signInWithPopup(auth, provider);
            if(result) {
                const firebaseUser = result.user;
                const idToken = await firebaseUser.getIdToken();

                // Split the full name into first and last name
                const { name, last_name } = splitFullName(firebaseUser.displayName || firebaseUser.email);

                await axios.post(
                    endpoints.signup,
                    {
                        name,
                        last_name,
                        email: firebaseUser.email,
                        is_mentor: isGuest ? false : isMentor,
                        is_guest: isGuest,
                        is_confirmed: true,
                        ...(signupSource ? { source: signupSource } : {}),
                    },
                    {
                        headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
                    }
                );

                const whoami = await axios.get(endpoints.whoami, {
                    headers: { Authorization: `Bearer ${idToken}` },
                });
                dispatch(setUser({ ...whoami.data }));

                trackSignUp({
                    method: "microsoft",
                    role: isGuest ? "guest" : isMentor ? "mentor" : "mentee",
                    source: signupSource,
                });

                Message.success({ content: t('notification_messages.sign_up_success'), closable: true, duration: 3000 });
                completeAuthRedirect(redirectPath ?? routes.profile);
            }
        } catch (error) {
            if (error instanceof FirebaseError && error.code === "auth/account-exists-with-different-credential") {
                Message.error(t('notification_messages.email_exists_with_google_error'));
                return;
            }

            if (isAuthPopupClosedByUser(error)) {
                console.error("Outlook registration failed:", error);
                return;
            }  

            if (error instanceof Error && error.message === "EMAIL_EXISTS") {
                console.error("Outlook error - email already in use:", error);
                Message.error(t('notification_messages.email_exists_error'));
            } else {
                console.error("Outlook registration failed:", error);
                Message.error(t('notification_messages.sign_up_error'));
            }
        }
    };

    if (variant === "icon") {
        return (
            <Button
                className="auth-sso-btn"
                onClick={handleMicrosoftRegister}
                icon={<WindowsOutlined />}
                aria-label={t('register_page.outlook_sign_up')}
            />
        );
    }

    return (
        <Button
            onClick={handleMicrosoftRegister}
            shape="round"
            size="large"
            icon={<WindowsOutlined />}
            {...getOAuthButtonProps()}
        >
           {t('register_page.outlook_sign_up')}
        </Button>
    );
};
